import React, { Component, ChangeEvent, KeyboardEvent } from 'react';
import InputWithLabel from './common/InputWithLabel';
import LoadingButtonBase from './common/LoadingButtonBase';
import { Alert } from 'reactstrap';
import { History } from 'history';
import { parseResponse } from '../utility/FetchFunctions';
import { FetchSuccess, FetchError } from '../types/FetchTypes';

type GrossReceiptAccountSearchProps = {
    history: History;
};

type GrossReceiptAccountSearchState = {
    accountNum: string;
    pin: string;
    error: string | null;
    loading: boolean;
};

type StatesUpdateableOnChange = keyof Pick<GrossReceiptAccountSearchState, 'accountNum' | 'pin'>;

const accountLockoutStatusCode = 555;

interface GrossReceiptAccountSummary {
    accountNumber: number;
    ownerName: string | null;
    ownerAddress: string | null;
    grossReceiptType: string | null;
    isOpen: boolean | null;
    locations: any[] | null;
    creditRemaining: number;
    grossReceiptTypeCode: string | null;
    splitVehicleUnderByYear: { [key: number]: number | null };
    splitVehicleOverByYear: { [key: number]: number | null };
};

export default class GrossReceiptAccountSearch extends Component<GrossReceiptAccountSearchProps, GrossReceiptAccountSearchState> {
    state: GrossReceiptAccountSearchState = {
        accountNum: '',
        pin: '',
        error: null,
        loading: false,
    };

    static displayName = GrossReceiptAccountSearch.name;

    filterNumberInput = (event: ChangeEvent<HTMLInputElement>, allowedDigits: number, stateProp: StatesUpdateableOnChange) => {
        const newValue = event.target.value;
        // Create a regex that only allows the provided number of digits
        const regexPrefix = /^\d{0,/;
        const regexSuffix = /}$/;
        if (newValue.match(regexPrefix.source + allowedDigits + regexSuffix.source)) {
            this.setState({
                [stateProp]: newValue
            } as Pick<GrossReceiptAccountSearchState, StatesUpdateableOnChange>);
        }
    };

    validateInput = () => {
        if (this.state.accountNum === '') {
            return "Please supply an account number.";
        } else if (this.state.pin === '') {
            return "Please supply a PIN.";
        } else if (this.state.pin.length !== 4) {
            return "PIN must be 4 digits.";
        }
    };

    trySearchingForAccount = async () => {
        this.setState({ loading: true });
        let error = this.validateInput() || null;
        if (!error) {
            const response = await this.fetchAccount();

            if (response.ok) {
                const account = await response.getValue?.();
                if (account) {
                    return this.props.history.push('/enter-return', {
                        accountNum: account.accountNumber,
                        pin: this.state.pin,
                        grossReceiptType: account.grossReceiptType,
                        ownerName: account.ownerName,
                        ownerAddress: account.ownerAddress,
                        locations: account.locations,
                        grossReceiptTypeCode: account.grossReceiptTypeCode,
                        creditRemaining: account.creditRemaining,
                        splitVehicleUnderByYear: account.splitVehicleUnderByYear,
                        splitVehicleOverByYear: account.splitVehicleOverByYear,
                    });
                } else {
                    error = "Account/PIN combination was not found.";
                }
            } else if (response.status === accountLockoutStatusCode) {
                error = `Account ${this.state.accountNum} has been locked due to too many unsuccessful login attempts.  Please try again later.`;
            } else {
                error = "An error occurred while searching for an account.";
            }
        }
        this.setState({
            error: error,
            loading: false,
        });
    };

    fetchAccount = async (): Promise<FetchSuccess<GrossReceiptAccountSummary | null> | FetchError> => {
        const result = await fetch(`api/GrossReceiptReturn/GrossReceiptAccountSummary?accountNumber=${this.state.accountNum}&pin=${this.state.pin}`);
        return parseResponse(result);
    }

    handleKeyPress = (event: KeyboardEvent) => {
        if (event.key === "Enter") {
            this.trySearchingForAccount();
        }
    }

    render() {
        return (
            <div>
                {this.error}
                <div className="centered-container">
                    <div className="container-inputs">
                        <InputWithLabel
                            label="Account #"
                            type="text"
                            name="account"
                            id="accountNumber"
                            value={this.state.accountNum}
                            onChange={e => this.filterNumberInput(e, 10, 'accountNum')}
                            onKeyPress={this.handleKeyPress}
                        />
                        <InputWithLabel
                            label="PIN"
                            type="password"
                            name="pin"
                            id="pin"
                            value={this.state.pin}
                            onChange={e => this.filterNumberInput(e, 4, 'pin')}
                            onKeyPress={this.handleKeyPress}
                        />
                        <LoadingButtonBase label="Search" className="search-button" onClick={this.trySearchingForAccount} loading={this.state.loading} />
                    </div>
                </div>
            </div>
        );
    }

    get error() {
        return this.state.error ?
            (
                <Alert color="danger">
                    {this.state.error}
                </Alert>
            ) :
            (<></>);
    };
}
